import { useMemo } from 'react';
import { Text } from '@clay-run/glaze/text';
import type { NoteTarget } from '@shared/flow/notes';
import { NotesOptionRow, recordMark } from './NotesOptionRow';
import { NotesSectionedPicker } from './NotesSectionedPicker';
import type { SectionableOption } from './postingSections';

interface PostingOption extends SectionableOption {
  target: NoteTarget;
}

/** Shown only while the account's name is unknown. */
const ACCOUNT_LABEL_FALLBACK = 'This account';

export interface PostingToComboProps {
  /** The thread's account; null when a contact has none, which leaves only contacts to choose. */
  accountId: string | null;
  /** The thread's contacts (same account only; no cross-account destinations). */
  roster: { id: string; name: string }[];
  rosterTruncated: boolean;
  /** The account's name, shown on the account option; "This account" until it is known. */
  accountName?: string | null;
  /** Where the note will be written. */
  value: NoteTarget;
  onChange: (target: NoteTarget) => void;
}

/** The label a destination shows: the account's name ("This account" while unknown) or the contact's name. */
export function postingLabel(
  target: NoteTarget,
  roster: { id: string; name: string }[],
  accountName?: string | null,
): string {
  if (target.kind === 'account') return accountName?.trim() || ACCOUNT_LABEL_FALLBACK;
  return roster.find(r => r.id === target.id)?.name ?? 'Contact';
}

/**
 * Searchable destination picker for the composer ("Posting to"): Account, then People, each row with
 * its avatar. Glaze `Combobox variant="popover"` (the `table/FieldPicker.tsx` pattern): a button
 * trigger and a search box inside the popup. Filtering is ours (Glaze's combobox filters nothing).
 */
export function PostingToCombo({ accountId, roster, rosterTruncated, accountName, value, onChange }: PostingToComboProps) {
  const options = useMemo<PostingOption[]>(() => {
    const contacts = roster.map((r): PostingOption => ({
      key: `c:${r.id}`,
      label: r.name,
      section: 'people',
      target: { kind: 'contact', id: r.id, accountId },
    }));
    if (accountId === null) return contacts;
    const account: NoteTarget = { kind: 'account', id: accountId, accountId };
    return [{ key: `a:${accountId}`, label: postingLabel(account, roster, accountName), section: 'account', target: account }, ...contacts];
  }, [accountId, roster, accountName]);

  const selected = options.find(o => o.target.kind === value.kind && o.target.id === value.id) ?? null;

  return (
    <div className="min-w-0 flex-1">
      <NotesSectionedPicker
        options={options}
        selected={selected}
        onSelect={o => onChange(o.target)}
        aria-label="Posting to"
        placeholder={postingLabel(value, roster, accountName)}
        searchPlaceholder="Search destinations"
        popupWidth="trigger"
        renderValue={o => <NotesOptionRow label={o.label} mark={recordMark(o.target.kind, o.target.id)} />}
        renderRow={o => <NotesOptionRow label={o.label} mark={recordMark(o.target.kind, o.target.id)} />}
        footer={() =>
          rosterTruncated && (
            <Text size="xs" variant="tertiary" className="block px-2.5 py-1.5">
              Showing the first 300 contacts
            </Text>
          )
        }
      />
    </div>
  );
}

/**
 * Replaces the generated leading `@<from> ` with `@<to> ` (either may be null
 * for the account, which has no prefix). Only a LEADING prefix is touched,
 * so `@Name` text the rep typed elsewhere in the note survives.
 */
export function swapPostingPrefix(draft: string, from: string | null, to: string | null): string {
  const oldPrefix = from === null ? null : `@${from} `;
  const rest = oldPrefix !== null && draft.startsWith(oldPrefix) ? draft.slice(oldPrefix.length) : draft;
  return to === null ? rest : `@${to} ${rest}`;
}
